/**
 * I-589 Asylum Application PDF Form Filler
 *
 * Fills the official USCIS I-589 PDF form using pdf-lib.
 * The blank PDF must be accessible at /forms/i-589-blank.pdf in the public folder.
 *
 * Field name reference: src/data/legalTemplates/forms/i589-field-map.txt
 */

import { PDFDocument, PDFTextField, PDFCheckBox } from 'pdf-lib';

// pdf-lib exports PDFForm as a default export, so we derive the type from an instance
type PDFFormType = ReturnType<InstanceType<typeof PDFDocument>['getForm']>;

// ============================================================================
// Data Types
// ============================================================================

export interface I589FormData {
  // Part A.I — Applicant Identity
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  aliases?: string;
  dob?: string; // MM/DD/YYYY
  city_of_birth?: string;
  country_of_birth?: string;
  nationality?: string;
  race_ethnicity?: string;
  religion?: string;
  gender?: 'male' | 'female' | string;
  marital_status?: 'single' | 'married' | 'divorced' | 'widowed' | string;

  // Part A.I — Immigration
  a_number?: string;
  ssn?: string;
  i94_number?: string;
  last_entry_date?: string; // MM/DD/YYYY
  last_entry_place?: string;
  immigration_status?: string;
  authorized_stay_expiry?: string;

  // Part A.I — Address
  residence_street?: string;
  residence_city?: string;
  residence_state?: string;
  residence_zip?: string;
  residence_phone?: string;

  // Part A.II — Spouse
  spouse_last_name?: string;
  spouse_first_name?: string;
  spouse_middle_name?: string;
  spouse_dob?: string;
  spouse_nationality?: string;
  spouse_a_number?: string;
  spouse_in_us?: 'true' | 'false' | string;

  // Part A.II — Children (up to 4)
  num_children?: string;
  child1_last_name?: string;
  child1_first_name?: string;
  child1_dob?: string;
  child1_nationality?: string;
  child1_in_us?: 'true' | 'false' | string;
  child2_last_name?: string;
  child2_first_name?: string;
  child2_dob?: string;
  child2_nationality?: string;
  child3_last_name?: string;
  child3_first_name?: string;
  child3_dob?: string;
  child3_nationality?: string;
  child4_last_name?: string;
  child4_first_name?: string;
  child4_dob?: string;
  child4_nationality?: string;

  // Part A.III — Background
  last_country_before_us?: string;
  travel_history?: string;
  previous_applications?: 'true' | 'false' | string;
  previous_applications_detail?: string;
  education_history?: string;
  employment_history?: string;

  // Part B — Persecution
  persecution_grounds?: string;
  persecution_narrative?: string;
  persecution_past?: 'true' | 'false' | string;
  persecution_fear_return?: 'true' | 'false' | string;

  // Part C — Additional
  other_country_claim?: 'true' | 'false' | string;
  other_country_detail?: string;
  immigration_proceedings?: 'true' | 'false' | string;

  // Part E — Attorney
  attorney_name?: string;
  attorney_bar_number?: string;
  attorney_street?: string;
  attorney_city?: string;
  attorney_state?: string;
  attorney_zip?: string;
}

// ============================================================================
// Helpers
// ============================================================================

function trySetTextField(form: PDFFormType, fieldName: string, value: string | undefined): void {
  if (!value) return;
  try {
    const field = form.getField(fieldName);
    if (field instanceof PDFTextField) {
      field.setText(value);
    }
  } catch {
    // Field may not exist in this PDF version — skip silently
  }
}

function tryCheckBox(form: PDFFormType, fieldName: string, shouldCheck: boolean): void {
  if (!shouldCheck) return;
  try {
    const field = form.getField(fieldName);
    if (field instanceof PDFCheckBox) {
      field.check();
    }
  } catch {
    // Field may not exist — skip silently
  }
}

/** Split a phone string like "(213) 555-0100" into area code + number */
function splitPhone(phone: string): { areaCode: string; number: string } {
  if (!phone) return { areaCode: '', number: '' };
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 10) {
    return { areaCode: digits.slice(0, 3), number: digits.slice(3, 10) };
  }
  return { areaCode: '', number: digits };
}

/** Truncate text with a note if it exceeds maxLen characters */
function truncateWithNote(text: string, maxLen: number): string {
  if (!text || text.length <= maxLen) return text;
  const note = ' [CONTINUED ON SUPPLEMENT]';
  return text.slice(0, maxLen - note.length) + note;
}

// ============================================================================
// Main Filler
// ============================================================================

/**
 * Fill the USCIS I-589 PDF form with the provided data.
 * Fetches the blank form from /forms/i-589-blank.pdf (public folder).
 * Returns the filled PDF as a Uint8Array.
 */
export async function fillI589Form(data: I589FormData): Promise<Uint8Array> {
  // Load blank PDF from public folder
  const pdfUrl = '/forms/i-589-blank.pdf';
  let pdfBytes: ArrayBuffer;

  try {
    const response = await fetch(pdfUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch I-589 blank form: ${response.status} ${response.statusText}`);
    }
    pdfBytes = await response.arrayBuffer();
  } catch (err) {
    throw new Error(`Could not load I-589 blank PDF from ${pdfUrl}: ${(err as Error).message}`);
  }

  // Load PDF document (form is already decrypted)
  const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  const form = pdfDoc.getForm();

  // --------------------------------------------------------------------------
  // Part A.I — Applicant Information (subform[0])
  // --------------------------------------------------------------------------

  trySetTextField(form, 'form1[0].#subform[0].PtAILine1_ANumber[0]', data.a_number);
  trySetTextField(form, 'form1[0].#subform[0].PtAILine4_LastName[0]', data.last_name);
  trySetTextField(form, 'form1[0].#subform[0].PtAILine5_FirstName[0]', data.first_name);
  trySetTextField(form, 'form1[0].#subform[0].PtAILine6_MiddleName[0]', data.middle_name);

  // DOB — DateTimeField1[0]
  trySetTextField(form, 'form1[0].#subform[0].DateTimeField1[0]', data.dob);

  // Aliases / other names — TextField1[1]
  trySetTextField(form, 'form1[0].#subform[0].TextField1[1]', data.aliases);

  // City of birth — TextField1[3]
  trySetTextField(form, 'form1[0].#subform[0].TextField1[3]', data.city_of_birth);

  // Country of birth — TextField1[4]
  trySetTextField(form, 'form1[0].#subform[0].TextField1[4]', data.country_of_birth);

  // Nationality — TextField1[5]
  trySetTextField(form, 'form1[0].#subform[0].TextField1[5]', data.nationality);

  // Race/Ethnic group — TextField1[6]
  trySetTextField(form, 'form1[0].#subform[0].TextField1[6]', data.race_ethnicity);

  // Religion — TextField3[0]
  trySetTextField(form, 'form1[0].#subform[0].TextField3[0]', data.religion);

  // Gender checkboxes
  if (data.gender) {
    tryCheckBox(form, 'form1[0].#subform[0].PartALine9Sex[0]', data.gender === 'male');
    tryCheckBox(form, 'form1[0].#subform[0].PartALine9Sex[1]', data.gender === 'female');
  }

  // Marital status checkboxes: [0]=Single [1]=Married [2]=Divorced [3]=Widowed
  if (data.marital_status) {
    const ms = data.marital_status.toLowerCase();
    tryCheckBox(form, 'form1[0].#subform[0].Marital[0]', ms === 'single');
    tryCheckBox(form, 'form1[0].#subform[0].Marital[1]', ms === 'married');
    tryCheckBox(form, 'form1[0].#subform[0].Marital[2]', ms === 'divorced');
    tryCheckBox(form, 'form1[0].#subform[0].Marital[3]', ms === 'widowed');
  }

  // Residence address
  trySetTextField(form, 'form1[0].#subform[0].PtAILine8_StreetNumandName[0]', data.residence_street);
  trySetTextField(form, 'form1[0].#subform[0].TextField1[2]', data.residence_city);
  trySetTextField(form, 'form1[0].#subform[0].PtAILine8_State[0]', data.residence_state);
  trySetTextField(form, 'form1[0].#subform[0].PtAILine8_Zipcode[0]', data.residence_zip);

  if (data.residence_phone) {
    const { areaCode, number } = splitPhone(data.residence_phone);
    trySetTextField(form, 'form1[0].#subform[0].PtAILine8_AreaCode[0]', areaCode);
    trySetTextField(form, 'form1[0].#subform[0].PtAILine8_TelephoneNumber[0]', number);
  }

  // SSN — TextField5[0]
  trySetTextField(form, 'form1[0].#subform[0].TextField5[0]', data.ssn);

  // I-94 Number — TextField5[1]
  trySetTextField(form, 'form1[0].#subform[0].TextField5[1]', data.i94_number);

  // Immigration status — TextField4[0]
  trySetTextField(form, 'form1[0].#subform[0].TextField4[0]', data.immigration_status);

  // Last entry date — DateTimeField2[0]
  trySetTextField(form, 'form1[0].#subform[0].DateTimeField2[0]', data.last_entry_date);

  // Last entry place — TextField4[1]
  trySetTextField(form, 'form1[0].#subform[0].TextField4[1]', data.last_entry_place);

  // Authorized stay expiry — DateTimeField3[0]
  trySetTextField(form, 'form1[0].#subform[0].DateTimeField3[0]', data.authorized_stay_expiry);

  // Last country before US — TextField7[0]
  trySetTextField(form, 'form1[0].#subform[0].TextField7[0]', data.last_country_before_us);

  // --------------------------------------------------------------------------
  // Part A.II — Spouse (subform[1].NotMarried[0])
  // --------------------------------------------------------------------------

  if (data.spouse_last_name) {
    trySetTextField(form, 'form1[0].#subform[1].NotMarried[0].PtAIILine5_LastName[0]', data.spouse_last_name);
    trySetTextField(form, 'form1[0].#subform[1].NotMarried[0].PtAIILine6_FirstName[0]', data.spouse_first_name);
    trySetTextField(form, 'form1[0].#subform[1].NotMarried[0].PtAIILine7_MiddleName[0]', data.spouse_middle_name);
    trySetTextField(form, 'form1[0].#subform[1].NotMarried[0].DateTimeField8[0]', data.spouse_dob);
    trySetTextField(form, 'form1[0].#subform[1].NotMarried[0].TextField10[0]', data.spouse_nationality);
    trySetTextField(form, 'form1[0].#subform[1].NotMarried[0].PtAIILine1_ANumber[0]', data.spouse_a_number);

    // Spouse in US checkbox
    if (data.spouse_in_us === 'true') {
      tryCheckBox(form, 'form1[0].#subform[1].NotMarried[0].PtAIILine22_Yes[0]', true);
    } else if (data.spouse_in_us === 'false') {
      tryCheckBox(form, 'form1[0].#subform[1].NotMarried[0].PtAIILine22_No[0]', true);
    }
  }

  // --------------------------------------------------------------------------
  // Part A.II — Children (subform[1] for child 1, subform[3] for children 2-4)
  // --------------------------------------------------------------------------

  // Total children count
  trySetTextField(form, 'form1[0].#subform[1].TotalChild[0]', data.num_children);

  // Child 1 (subform[1])
  if (data.child1_last_name) {
    trySetTextField(form, 'form1[0].#subform[1].ChildLast1[0]', data.child1_last_name);
    trySetTextField(form, 'form1[0].#subform[1].ChildFirst1[0]', data.child1_first_name);
    trySetTextField(form, 'form1[0].#subform[1].ChildDOB1[0]', data.child1_dob);
    trySetTextField(form, 'form1[0].#subform[1].ChildNat1[0]', data.child1_nationality);
    if (data.child1_in_us === 'true') {
      tryCheckBox(form, 'form1[0].#subform[1].PtAIILine20_Yes[0]', true);
    } else if (data.child1_in_us === 'false') {
      tryCheckBox(form, 'form1[0].#subform[1].PtAIILine20_No[0]', true);
    }
  }

  // Child 2 (subform[3])
  if (data.child2_last_name) {
    trySetTextField(form, 'form1[0].#subform[3].ChildLast2[0]', data.child2_last_name);
    trySetTextField(form, 'form1[0].#subform[3].ChildFirst2[0]', data.child2_first_name);
    trySetTextField(form, 'form1[0].#subform[3].ChildDOB2[0]', data.child2_dob);
    trySetTextField(form, 'form1[0].#subform[3].ChildNat2[0]', data.child2_nationality);
  }

  // Child 3 (subform[3])
  if (data.child3_last_name) {
    trySetTextField(form, 'form1[0].#subform[3].ChildLast3[0]', data.child3_last_name);
    trySetTextField(form, 'form1[0].#subform[3].ChildFirst3[0]', data.child3_first_name);
    trySetTextField(form, 'form1[0].#subform[3].ChildDOB3[0]', data.child3_dob);
    trySetTextField(form, 'form1[0].#subform[3].ChildNat3[0]', data.child3_nationality);
  }

  // Child 4 (subform[3])
  if (data.child4_last_name) {
    trySetTextField(form, 'form1[0].#subform[3].ChildLast4[0]', data.child4_last_name);
    trySetTextField(form, 'form1[0].#subform[3].ChildFirst4[0]', data.child4_first_name);
    trySetTextField(form, 'form1[0].#subform[3].ChildDOB4[0]', data.child4_dob);
    trySetTextField(form, 'form1[0].#subform[3].ChildNat4[0]', data.child4_nationality);
  }

  // --------------------------------------------------------------------------
  // Part B — Persecution Claim (subform[5] and subform[7])
  // --------------------------------------------------------------------------

  // Persecution grounds checkboxes (subform[5].#subform[6])
  if (data.persecution_grounds) {
    const grounds = data.persecution_grounds.toLowerCase();
    tryCheckBox(form, 'form1[0].#subform[5].#subform[6].CheckBoxrace[0]', grounds.includes('race'));
    tryCheckBox(form, 'form1[0].#subform[5].#subform[6].CheckBoxreligion[0]', grounds.includes('religion'));
    tryCheckBox(form, 'form1[0].#subform[5].#subform[6].CheckBoxnationality[0]', grounds.includes('nationality'));
    tryCheckBox(form, 'form1[0].#subform[5].#subform[6].CheckBoxpolitics[0]',
      grounds.includes('political') || grounds.includes('opinion'));
    tryCheckBox(form, 'form1[0].#subform[5].#subform[6].CheckBoxsocial[0]',
      grounds.includes('social'));
    tryCheckBox(form, 'form1[0].#subform[5].#subform[6].CheckBoxtorture[0]',
      grounds.includes('torture'));
  }

  // Past persecution checkbox (subform[7])
  if (data.persecution_past === 'true') {
    tryCheckBox(form, 'form1[0].#subform[7].ckboxyn2[0]', true);
  } else if (data.persecution_past === 'false') {
    tryCheckBox(form, 'form1[0].#subform[7].ckboxyn2[1]', true);
  }

  // Fear of return checkbox
  if (data.persecution_fear_return === 'true') {
    tryCheckBox(form, 'form1[0].#subform[7].ckboxyn3a[0]', true);
  } else if (data.persecution_fear_return === 'false') {
    tryCheckBox(form, 'form1[0].#subform[7].ckboxyn3a[1]', true);
  }

  // Persecution narrative — primary text field (subform[7].PBL2_TextField[0])
  // This field has limited space; truncate with supplement notice if needed
  if (data.persecution_narrative) {
    const truncated = truncateWithNote(data.persecution_narrative, 3000);
    trySetTextField(form, 'form1[0].#subform[7].PBL2_TextField[0]', truncated);

    // Also fill the continuation field if it exists
    if (data.persecution_narrative.length > 3000) {
      const continuation = data.persecution_narrative.slice(3000 - '[CONTINUED ON SUPPLEMENT]'.length);
      trySetTextField(form, 'form1[0].#subform[7].PBL3A_TextField[0]', truncateWithNote(continuation, 2000));
    }
  }

  // --------------------------------------------------------------------------
  // Part C — Additional Claims (subform[8] and subform[9])
  // --------------------------------------------------------------------------

  // Other country withholding claim
  if (data.other_country_claim === 'true') {
    tryCheckBox(form, 'form1[0].#subform[8].ckboxync1[0]', true);
    trySetTextField(form, 'form1[0].#subform[8].PCL1_TextField[0]', data.other_country_detail);
  } else if (data.other_country_claim === 'false') {
    tryCheckBox(form, 'form1[0].#subform[8].ckboxync1[1]', true);
  }

  // Immigration proceedings
  if (data.immigration_proceedings === 'true') {
    tryCheckBox(form, 'form1[0].#subform[8].ckboxync2a[0]', true);
  } else if (data.immigration_proceedings === 'false') {
    tryCheckBox(form, 'form1[0].#subform[8].ckboxync2a[1]', true);
  }

  // --------------------------------------------------------------------------
  // Part E — Attorney / Preparer (subform[10])
  // --------------------------------------------------------------------------

  if (data.attorney_name) {
    trySetTextField(form, 'form1[0].#subform[10].PtE_PreparerName[0]', data.attorney_name);
    trySetTextField(form, 'form1[0].#subform[10].PtE_StreetNumAndName[0]', data.attorney_street);
    trySetTextField(form, 'form1[0].#subform[10].PtE_City[0]', data.attorney_city);
    trySetTextField(form, 'form1[0].#subform[10].PtE_State[0]', data.attorney_state);
    trySetTextField(form, 'form1[0].#subform[10].PtE_ZipCode[0]', data.attorney_zip);
    trySetTextField(form, 'form1[0].#subform[10].AttorneyStateBarNumber[0]', data.attorney_bar_number);
  }

  // --------------------------------------------------------------------------
  // Flatten and serialize
  // --------------------------------------------------------------------------
  const filledBytes = await pdfDoc.save();
  return filledBytes;
}

/**
 * Convert I589FormData from the legalDocCreator collectedFields format
 * (all values as strings) into typed I589FormData.
 */
export function fieldsToI589Data(fields: Record<string, string>): I589FormData {
  return fields as I589FormData;
}
